import { combineReducers } from 'redux';
import { CALL_API, getJSON } from 'redux-api-middleware';
import { List, Map, fromJS } from 'immutable';
import { normalize } from 'normalizr';
import { camelizeKeys } from 'humps';
import { createSelector } from 'reselect';
import { isArray } from 'lodash';
import schemas from './schemas';
import { getLeagueStr } from '../utils';

// Constants

export const API_ROOT = (process.env.NODE_ENV === 'production') ?
                  'https://api.s4upro.com' : 'http://localhost:8000';

export const API_REQUEST_FAILURE = 'API_REQUEST_FAILURE';
export const API_REQUEST_EMPTY_ACTION = 'API_REQUEST_EMPTY_ACTION';

const LOGIN_REQUEST = 'LOGIN_REQUEST';
const LOGIN_SUCCESS = 'LOGIN_SUCCESS';
const LOGIN_FAILURE = 'LOGIN_FAILURE';

const SEASONS_REQUEST = 'SEASONS_REQUEST';
const SEASONS_SUCCESS = 'SEASONS_SUCCESS';
const SELECT_SEASON = 'SELECT_SEASON';

const LEAGUES_BY_SEASON_REQUEST = 'LEAGUES_BY_SEASON_REQUEST';
const LEAGUES_BY_SEASON_SUCCESS = 'LEAGUES_BY_SEASON_SUCCESS';
const SELECT_LEAGUES = 'SELECT_LEAGUES';

const TEAMS_BY_LEAGUE_REQUEST = 'TEAMS_BY_LEAGUE_REQUEST';
const TEAMS_BY_LEAGUE_SUCCESS = 'TEAMS_BY_LEAGUE_SUCCESS';
const SELECT_TEAMS = 'SELECT_TEAMS';

// Action creators helpers

export const getDefaultRequestHeaders = () => (
  (window.sessionStorage.accessToken && window.sessionStorage.accessToken !== '') ? {
    'Content-Type': 'application/json',
    'Authorization': `JWT ${window.sessionStorage.accessToken}`
  } : {
    'Content-Type': 'application/json'
  });

export const getDefaultAPIRequestErrorFSA = () => ([{
  type: API_REQUEST_FAILURE,
  meta: (action, state, res) => {
    if (res) {
      return {
        status: res.status,
        statusText: res.statusText,
        token: window.sessionStorage.accessToken
      };
    } else {
      return {
        status: 'Network request failed',
        token: window.sessionStorage.accessToken
      };
    }
  }
}]);

// Action creators

export const loginUser = (email, password) => ({
  [CALL_API]: {
    endpoint: `${API_ROOT}/api-token-auth/`,
    method: 'POST',
    headers: getDefaultRequestHeaders(),
    body: JSON.stringify({ email, password }),
    types: [
      LOGIN_REQUEST,
      {
        type: LOGIN_SUCCESS,
        payload: (action, state, res) => {
          return getJSON(res)
            .then((json) => {
              return {
                ...json
              };
            });
        }
      },
      LOGIN_FAILURE
    ]
  }
});

export const fetchSeasons = () => ({
  [CALL_API]: {
    endpoint: `${API_ROOT}/v1/seasons/`,
    method: 'GET',
    headers: getDefaultRequestHeaders(),
    bailout: (state) => (
      state.core.seasons.has('items') ||
      state.core.seasons.get('isFetching')
    ),
    types: [
      SEASONS_REQUEST,
      {
        type: SEASONS_SUCCESS,
        payload: (action, state, res) => {
          return getJSON(res)
            .then((json) => {
              const camelizedJson = camelizeKeys(json);
              const normalized = normalize(camelizedJson, schemas.SEASON_ARRAY);
              return {
                entities: normalized.entities,
                items: normalized.result
              };
            });
        }
      },
      ...getDefaultAPIRequestErrorFSA()
    ]
  }
});

export const selectSeason = seasonId => ({
  type: SELECT_SEASON,
  seasonId
});

export const fetchLeaguesBySeason = seasonId => ({
  [CALL_API]: {
    endpoint: `${API_ROOT}/v1/seasons/${seasonId}/leagues/`,
    method: 'GET',
    headers: getDefaultRequestHeaders(),
    bailout: (state) => (
      !seasonId
      || state.core.leaguesBySeason.hasIn([seasonId, 'items'])
      || state.core.leaguesBySeason.getIn([seasonId, 'isFetching'])
    ),
    types: [
      {
        type: LEAGUES_BY_SEASON_REQUEST,
        payload: { seasonId }
      },
      {
        type: LEAGUES_BY_SEASON_SUCCESS,
        payload: (action, state, res) => {
          return getJSON(res)
            .then((json) => {
              const camelizedJson = camelizeKeys(json);
              const normalized = normalize(camelizedJson, schemas.LEAGUE_ARRAY);
              return {
                seasonId,
                entities: normalized.entities,
                items: normalized.result
              };
            });
        }
      },
      ...getDefaultAPIRequestErrorFSA()
    ]
  }
});

export const selectLeagues = leagueIds => ({
  type: SELECT_LEAGUES,
  leagueIds
});

export const fetchTeamsByLeague = (seasonId, leagueIds) => {
  const leagues = isArray(leagueIds) ? leagueIds.join(",") : "";
  return {
    [CALL_API]: {
      endpoint: `${API_ROOT}/v1/seasons/${seasonId}/leagues/${leagues}/teams/`,
      method: 'GET',
      headers: getDefaultRequestHeaders(),
      bailout: (state) => (
        !seasonId
        || (leagues === "")
        || state.core.teamsByLeague.hasIn([seasonId, leagues, 'items'])
        || state.core.teamsByLeague.getIn([seasonId, leagues, 'isFetching'])
      ),
      types: [
        {
          type: TEAMS_BY_LEAGUE_REQUEST,
          payload: { seasonId, leagues }
        },
        {
          type: TEAMS_BY_LEAGUE_SUCCESS,
          payload: (action, state, res) => {
            return getJSON(res)
              .then((json) => {
                const camelizedJson = camelizeKeys(json);
                const normalized = normalize(camelizedJson, schemas.TEAM_ARRAY);
                return {
                  seasonId,
                  leagueIds: leagues,
                  entities: normalized.entities,
                  items: normalized.result
                };
              });
          }
        },
        ...getDefaultAPIRequestErrorFSA()
      ]
    }
  };
};

export const selectTeams = teamIds => ({
  type: SELECT_TEAMS,
  teamIds
});


// Reducers

const auth = (state = Map({
  isFetching: false,
  errorMsg: '',
  user: null
}), action) => {
  switch (action.type) {
    case LOGIN_REQUEST:
      return state.set('isFetching', true)
                  .set('errorMsg', '');
    case LOGIN_SUCCESS:
      window.sessionStorage.accessToken = action.payload.token;
      return state.set('isFetching', false)
                  .set('user', fromJS(action.payload.user));
    case LOGIN_FAILURE:
      return state.set('isFetching', false)
                  .set('errorMsg', 'Email and password do not match, please try again:');
    case API_REQUEST_FAILURE:
      switch (action.meta.status) {
        case 401:
          const token = action.meta.token;
          const errorMsg = (!token || token === '')  ? '' : 'Your session has expired, please login again:';
          window.sessionStorage.accessToken = '';
          return state.set('errorMsg', errorMsg)
                      .set('user', null);
        case 403:
          window.sessionStorage.accessToken = '';
          return state.set('errorMsg', 'You are not subscribed to this package, would you like to login with a different account?')
                      .set('user', null);
        default:
          return state;
      }
    default:
      return state;
  }
};

const userContext = (state = Map(), action) => {
  switch (action.type) {
    case SEASONS_SUCCESS:  // Selects first season by default
      if (!state.get('seasonId')) {
        return state.set('seasonId', action.payload.items[0]);
      }
      return state;
    case SELECT_SEASON:
      return state.set('seasonId', action.seasonId);
    case SELECT_LEAGUES:
      return state.set('leagueIds', List(action.leagueIds));
    case SELECT_TEAMS:
      return state.set('teamIds', List(action.teamIds));
    default:
      return state;
  }
};

const entities = (state = Map(), action) => {
  const { payload } = action;
  if (payload && payload.entities) {
    return state.mergeDeep(fromJS(payload.entities));
  }
  return state;
};

const seasons = (state = Map({ isFetching: false }), action) => {
  const { payload } = action;
  switch (action.type) {
    case SEASONS_REQUEST:
      return state.set('isFetching', true);
    case SEASONS_SUCCESS:
      return state.set('isFetching', false)
                  .set('items', List(payload.items));
    default:
      return state;
  }
};

const leaguesBySeason = (state = Map(), action) => {
  const { payload } = action;
  switch (action.type) {
    case LEAGUES_BY_SEASON_REQUEST:
      return state.set(payload.seasonId, Map({ isFetching: true }));
    case LEAGUES_BY_SEASON_SUCCESS:
      return state.set(payload.seasonId, fromJS({
        isFetching: false,
        items: payload.items
       }));
    default:
      return state;
  }
};

const teamsByLeague = (state = Map(), action) => {
  const { payload } = action;
  switch (action.type) {
    case TEAMS_BY_LEAGUE_REQUEST:
      return state.setIn([payload.seasonId, payload.leagueIds], Map({
        isFetching: true
      }));
    case TEAMS_BY_LEAGUE_SUCCESS:
      return state.setIn([payload.seasonId, payload.leagueIds], fromJS({
        isFetching: false,
        items: payload.items
       }));
    default:
      return state;
  }
};

export default combineReducers({
  auth,
  userContext,
  entities,
  seasons,
  leaguesBySeason,
  teamsByLeague
});

// Selectors

export const getStateSeasonEntities = (state) => state.core.entities.get('seasons');
export const getStateLeagueEntities = (state) => state.core.entities.get('leagues');
export const getStateCountryEntities = (state) => state.core.entities.get('countries');
export const getStateTeamEntities = (state) => state.core.entities.get('teams');
export const getStatePlayerEntities = (state) => state.core.entities.get('players');
export const getStatePlayerPositionEntities = (state) => state.core.entities.get('playerPositions');

const getStateUserContext = (state) => state.core.userContext;

const getStateSeasons = (state) => state.core.seasons;
const getStateLeaguesBySeason = (state) => state.core.leaguesBySeason;
const getStateTeamsByLeague = (state) => state.core.teamsByLeague;

export const getSeasons = createSelector(
  [ getStateSeasons, getStateSeasonEntities ],
  (seasonsMap, seasonEntities) => {
    const seasons = seasonsMap.toJS();

    return {
      isFetching: seasons.isFetching,
      items: !seasons.items ? [] : seasons.items.map(season => ({
        key: season.toString(),
        value: seasonEntities.getIn([season.toString(), 'name'])
      }))
    };
  }
);

export const getSelectedSeason = createSelector(
  [ getStateUserContext ],
  (context) => context.get('seasonId', '').toString()
);

export const getLeaguesBySelectedSeason = createSelector(
  [ getSelectedSeason, getStateLeaguesBySeason, getStateLeagueEntities, getStateCountryEntities ],
  (selectedSeason, leaguesBySeasonMap, leagueEntities, countryEntities) => {
    const leaguesBySeason = leaguesBySeasonMap.toJS();

    if (!selectedSeason || !leaguesBySeason[selectedSeason]) {
      return { isFetching: false, items: [] };
    }

    const leagues = leaguesBySeason[selectedSeason];
    return {
      isFetching: leagues.isFetching,
      items: !leagues.items ? [] : leagues.items.map(league => {
        const leagueEntity = leagueEntities.get(league.toString()).toJS();
        const countryEntity = leagueEntity.country
                              ? countryEntities.get(leagueEntity.country.toString(), Map()).toJS()
                              : null;

        return {
          key: league.toString(),
          value: getLeagueStr(leagueEntity, countryEntity)
        };
      })
    };
  }
);

export const getSelectedLeagues = createSelector(
  [ getStateUserContext ],
  (context) => context.get('leagueIds', List()).toJS()
);

export const getTeamsBySelectedSeasonAndLeague = createSelector(
  [ getSelectedSeason, getSelectedLeagues, getStateTeamsByLeague, getStateTeamEntities ],
  (selectedSeason, selectedLeagues, teamsByLeagueMap, teamEntities) => {
    const teamsByLeague = teamsByLeagueMap.toJS();
    const leagues = selectedLeagues.join(",");

    if (!selectedSeason
      || !selectedLeagues.length
      || !teamsByLeague[selectedSeason]
      || !teamsByLeague[selectedSeason][leagues]) {
      return { isFetching: false, items: [] };
    }

    const teams = teamsByLeague[selectedSeason][leagues];
    return {
      isFetching: teams.isFetching,
      items: !teams.items ? [] : teams.items.map(team => ({
        key: team.toString(),
        value: teamEntities.getIn([team.toString(), 'name'])
      }))
    };
  }
);

export const getSelectedTeams = createSelector(
  [ getStateUserContext ],
  (context) => context.get('teamIds', List()).toJS()
);



// WEBPACK FOOTER //
// ./src/core/duck.js